import type { Position } from "~/v3-poc/api/positions.schema";
import { watchlistSymbolKey } from "~/v3-poc/utils/watchlistSymbol";
import type {
  PositionsTicketSeed,
  PositionsWidgetBadgeColor,
  PositionsWidgetFilters,
  PositionsWidgetRow,
  PositionsWidgetValueColor,
} from "./PositionsWidget.types";

export const POSITIONS_UNAVAILABLE_VALUE = "—";

const MILLISECONDS_PER_DAY = 86_400_000;

const quantityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const fractionalQuantityFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const expirationFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const finiteNumber = (value: unknown): number | undefined => {
  if (typeof value !== "number" && typeof value !== "string") {
    return undefined;
  }
  if (typeof value === "string" && value.trim() === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const displayText = (value: unknown): string =>
  typeof value === "string" && value.trim()
    ? value.trim()
    : POSITIONS_UNAVAILABLE_VALUE;

export const formatPositionLabel = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return POSITIONS_UNAVAILABLE_VALUE;
  }
  return value
    .trim()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

export const formatPositionQuantity = (value: unknown): string => {
  const quantity = finiteNumber(value);
  if (quantity === undefined) {
    return POSITIONS_UNAVAILABLE_VALUE;
  }
  return Number.isInteger(quantity)
    ? quantityFormatter.format(quantity)
    : fractionalQuantityFormatter.format(quantity);
};

export const formatPositionMoney = (value: unknown): string => {
  const amount = finiteNumber(value);
  return amount === undefined
    ? POSITIONS_UNAVAILABLE_VALUE
    : currencyFormatter.format(amount);
};

/** Signed USD, e.g. `+$100.00` / `-$42.00`, matching positions-next P&L cells. */
export const formatPositionSignedMoney = (value: unknown): string => {
  const amount = finiteNumber(value);
  if (amount === undefined) {
    return POSITIONS_UNAVAILABLE_VALUE;
  }
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${sign}${currencyFormatter.format(Math.abs(amount))}`;
};

export const formatPositionPercent = (value: unknown): string => {
  const ratio = finiteNumber(value);
  return ratio === undefined
    ? POSITIONS_UNAVAILABLE_VALUE
    : percentFormatter.format(ratio);
};

/** Signed percent from a fraction, e.g. `+6.67%` / `-1.50%`. */
export const formatPositionSignedPercent = (value: unknown): string => {
  const ratio = finiteNumber(value);
  if (ratio === undefined) {
    return POSITIONS_UNAVAILABLE_VALUE;
  }
  const points = ratio * 100;
  const sign = points > 0 ? "+" : points < 0 ? "-" : "";
  return `${sign}${Math.abs(points).toFixed(2)}%`;
};

/** Percentage from a fraction with one decimal, e.g. IV `0.284` → `28.4%`. */
export const formatPositionRatio = (value: unknown): string => {
  const ratio = finiteNumber(value);
  return ratio === undefined
    ? POSITIONS_UNAVAILABLE_VALUE
    : `${(ratio * 100).toFixed(1)}%`;
};

export const formatPositionGreek = (
  value: unknown,
  digits: number = 3,
): string => {
  const greek = finiteNumber(value);
  return greek === undefined
    ? POSITIONS_UNAVAILABLE_VALUE
    : greek.toFixed(digits);
};

export const formatPositionBoolean = (value: unknown): string =>
  typeof value === "boolean"
    ? value
      ? "Yes"
      : "No"
    : POSITIONS_UNAVAILABLE_VALUE;

export const formatPositionExpiration = (value: unknown): string => {
  if (typeof value !== "string" || !value.trim()) {
    return POSITIONS_UNAVAILABLE_VALUE;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp)
    ? expirationFormatter.format(timestamp)
    : POSITIONS_UNAVAILABLE_VALUE;
};

/** Calendar days until expiry; negative once expired. */
export const getPositionDaysToExpiry = (
  expiresAt: unknown,
  now: number = Date.now(),
): number | undefined => {
  if (typeof expiresAt !== "string" || !expiresAt.trim()) {
    return undefined;
  }
  const timestamp = Date.parse(expiresAt);
  if (!Number.isFinite(timestamp)) {
    return undefined;
  }
  return Math.ceil((timestamp - now) / MILLISECONDS_PER_DAY);
};

export const normalizePositionSide = (
  value: unknown,
): "long" | "short" | undefined => {
  const normalized =
    typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalized === "long") {
    return "long";
  }
  return normalized === "short" ? "short" : undefined;
};

export const getPositionSideBadgeColor = (
  value: unknown,
): PositionsWidgetBadgeColor => {
  const side = normalizePositionSide(value);
  if (side === "long") {
    return "green";
  }
  return side === "short" ? "red" : "gray";
};

export const getPositionValueColor = (
  value: unknown,
): PositionsWidgetValueColor => {
  const amount = finiteNumber(value);
  if (amount === undefined || amount === 0) {
    return "gray";
  }
  return amount < 0 ? "red" : "green";
};

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Root, `YYMMDD`, `C`/`P`, strike in thousandths of a dollar. */
const OCC_SYMBOL_PATTERN = /^([A-Z]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/;

export interface CompactOptionContract {
  underlying: string;
  strikeAndType: string;
  shortExpiration: string;
}

/** Parses an OCC symbol into the compact table label, e.g. `220C Sep 18`. */
export const parseCompactOptionContract = (
  symbol: string,
): CompactOptionContract | null => {
  const match = OCC_SYMBOL_PATTERN.exec(symbol.trim().toUpperCase());
  if (!match) {
    return null;
  }
  const [, underlying, , month, day, right, strikeRaw] = match;
  const strike = Number.parseInt(strikeRaw, 10) / 1000;
  const monthLabel = MONTH_LABELS[Number(month) - 1];
  if (!monthLabel || !Number.isFinite(strike)) {
    return null;
  }
  const strikeLabel = Number.isInteger(strike)
    ? String(strike)
    : String(strike);
  return {
    underlying,
    strikeAndType: `${strikeLabel}${right}`,
    shortExpiration: `${monthLabel} ${Number(day)}`,
  };
};

export const formatCompactOptionContract = (
  contract: CompactOptionContract,
): string => `${contract.strikeAndType} ${contract.shortExpiration}`;

export const getPositionsBookTotal = (positions: readonly Position[]): number =>
  positions.reduce((sum, position) => {
    const value = finiteNumber(position.market_value);
    return value === undefined ? sum : sum + Math.abs(value);
  }, 0);

const formatPctOfPortfolio = (
  marketValue: number | undefined,
  bookTotal: number | undefined,
): string => {
  if (marketValue === undefined || bookTotal === undefined || bookTotal <= 0) {
    return POSITIONS_UNAVAILABLE_VALUE;
  }
  return formatPositionPercent(Math.abs(marketValue) / bookTotal);
};

export const mapPositionToRow = (
  position: Position,
  now: number = Date.now(),
  bookTotal?: number,
): PositionsWidgetRow => {
  const symbol = displayText(position.symbol);
  const underlyingSymbol = displayText(position.underlying_symbol);
  const quantityValue = finiteNumber(position.qty);
  const daysToExpiry = getPositionDaysToExpiry(position.expires_at, now);
  const compact = parseCompactOptionContract(
    typeof position.symbol === "string" ? position.symbol : "",
  );
  const marketValueAmount = finiteNumber(position.market_value);
  const positionSide = normalizePositionSide(position.side);
  const isOption =
    typeof position.asset_class === "string" &&
    position.asset_class.trim().toLowerCase() === "us_option";
  const deltaValue =
    finiteNumber(position.delta) ??
    (isOption
      ? undefined
      : positionSide === "short"
        ? -1
        : positionSide === "long"
          ? 1
          : undefined);

  return {
    id: symbol,
    symbol,
    name: displayText(position.name),
    side: formatPositionLabel(position.side),
    quantity: formatPositionQuantity(position.qty),
    startOfDayQty: POSITIONS_UNAVAILABLE_VALUE,
    availableQty: formatPositionQuantity(position.qty_available),
    avgEntryPrice: formatPositionMoney(position.avg_entry_price),
    marketValue: formatPositionMoney(position.market_value),
    unrealizedPl: formatPositionSignedMoney(position.unrealized_pl),
    unrealizedPlpc: formatPositionSignedPercent(position.unrealized_plpc),
    assetClass: formatPositionLabel(position.asset_class),
    underlyingSymbol,
    expiresAt: formatPositionExpiration(position.expires_at),
    costBasis: formatPositionMoney(position.cost_basis),
    unrealizedIntradayPl: formatPositionSignedMoney(
      position.unrealized_intraday_pl,
    ),
    unrealizedIntradayPlpc: formatPositionSignedPercent(
      position.unrealized_intraday_plpc,
    ),
    currentPrice: formatPositionMoney(position.current_price),
    lastPrice: formatPositionMoney(position.lastday_price),
    currency: "USD",
    todaysTurnover: POSITIONS_UNAVAILABLE_VALUE,
    todayAvgBuyPrice: POSITIONS_UNAVAILABLE_VALUE,
    todayAvgSellPrice: POSITIONS_UNAVAILABLE_VALUE,
    initialMargin: POSITIONS_UNAVAILABLE_VALUE,
    exchange: displayText(position.exchange),
    assetMarginable: formatPositionBoolean(position.asset_marginable),
    intrinsicValue: formatPositionMoney(position.intrinsic_value),
    extrinsicValue: formatPositionMoney(position.extrinsic_value),
    delta: formatPositionGreek(deltaValue, 2),
    gamma: formatPositionGreek(position.gamma),
    vega: formatPositionGreek(position.vega),
    theta: formatPositionGreek(position.theta),
    rho: formatPositionGreek(position.rho),
    impliedVolatility: formatPositionRatio(position.implied_volatility),
    assetId: displayText(position.asset_id),
    sideColor: getPositionSideBadgeColor(position.side),
    unrealizedPlColor: getPositionValueColor(position.unrealized_pl),
    unrealizedPlpcColor: getPositionValueColor(position.unrealized_plpc),
    unrealizedIntradayPlColor: getPositionValueColor(
      position.unrealized_intraday_pl,
    ),
    unrealizedIntradayPlpcColor: getPositionValueColor(
      position.unrealized_intraday_plpc,
    ),
    positionSide,
    quantityValue:
      quantityValue === undefined ? undefined : Math.abs(quantityValue),
    intradayPlValue: finiteNumber(position.unrealized_intraday_pl),
    marketValueAmount,
    rollSymbol:
      underlyingSymbol === POSITIONS_UNAVAILABLE_VALUE
        ? symbol
        : underlyingSymbol,
    daysToExpiry,
    pctOfPortfolio: formatPctOfPortfolio(marketValueAmount, bookTotal),
    compactContract: compact
      ? formatCompactOptionContract(compact)
      : POSITIONS_UNAVAILABLE_VALUE,
  };
};

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export const createDefaultPositionsFilters = (): PositionsWidgetFilters => ({
  search: "",
  assetClass: "all",
  side: "all",
  maxDaysToExpiry: "all",
  positionState: "open",
  grouping: "flat",
  source: "all",
  riskPreset: "none",
  columnPreset: "default",
  watchlistOnly: false,
  flaggedOnly: false,
  stagedOnly: false,
});

const normalizedAssetClass = (row: PositionsWidgetRow): string =>
  row.assetClass.trim().toLowerCase().replaceAll(" ", "_");

export const isCryptoPositionRow = (row: PositionsWidgetRow): boolean =>
  normalizedAssetClass(row) === "crypto";

export const isOptionPositionRow = (row: PositionsWidgetRow): boolean =>
  normalizedAssetClass(row) === "us_option";

/** Drop separators so `BTC/USD`, `BTCUSD`, and `btc usd` all match. */
const normalizeSearchText = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * Symbol search matches the raw ticker and the drawn cell (option compact
 * label, crypto without the slash). The name column is left out on purpose —
 * the field says symbol, and a name hit with no visible ticker match reads
 * as a bug.
 */
export const matchesPositionsSearch = (
  row: PositionsWidgetRow,
  search: string,
): boolean => {
  const needle = normalizeSearchText(search);
  if (!needle) {
    return true;
  }
  const compact =
    row.compactContract === POSITIONS_UNAVAILABLE_VALUE
      ? ""
      : `${row.underlyingSymbol}${row.compactContract}`;
  return [row.symbol, compact].some((text) =>
    normalizeSearchText(text).includes(needle),
  );
};

export const positionsEmptyTableMessage = (search: string): string =>
  search.trim()
    ? `No positions match “${search.trim()}”.`
    : "No positions for this filter.";

export const positionsOpenCountLabel = (
  shown: number,
  total: number,
): string => (shown === total ? `${total} open` : `${shown} of ${total} open`);

export const matchesPositionsAssetClass = (
  row: PositionsWidgetRow,
  assetClass: PositionsWidgetFilters["assetClass"],
): boolean => assetClass === "all" || normalizedAssetClass(row) === assetClass;

export const matchesPositionsSide = (
  row: PositionsWidgetRow,
  side: PositionsWidgetFilters["side"],
): boolean => side === "all" || row.positionSide === side;

/**
 * A DTE limit is inherently an options filter, so rows without an expiry are
 * excluded once a limit is set rather than passing through unbounded.
 */
export const matchesPositionsDaysToExpiry = (
  row: PositionsWidgetRow,
  maxDaysToExpiry: string,
): boolean => {
  if (!maxDaysToExpiry.trim() || maxDaysToExpiry === "all") {
    return true;
  }
  const limit = Number(maxDaysToExpiry.trim());
  if (!Number.isFinite(limit)) {
    return true;
  }
  return row.daysToExpiry !== undefined && row.daysToExpiry <= limit;
};

export const matchesPositionsPositionState = (
  _row: PositionsWidgetRow,
  positionState: PositionsWidgetFilters["positionState"],
): boolean =>
  // The list endpoint only returns open positions; closed/filled are empty.
  positionState === "all" || positionState === "open";

export const filterPositionRows = (
  rows: readonly PositionsWidgetRow[],
  filters: PositionsWidgetFilters,
  watchedSymbols: ReadonlySet<string> = new Set(),
): PositionsWidgetRow[] =>
  rows.filter(
    (row) =>
      matchesPositionsSearch(row, filters.search) &&
      matchesPositionsAssetClass(row, filters.assetClass) &&
      matchesPositionsSide(row, filters.side) &&
      matchesPositionsDaysToExpiry(row, filters.maxDaysToExpiry) &&
      matchesPositionsPositionState(row, filters.positionState) &&
      (!filters.watchlistOnly ||
        watchedSymbols.has(watchlistSymbolKey(row.symbol))),
  );

const applyPositionsRiskPreset = (
  rows: PositionsWidgetRow[],
  preset: PositionsWidgetFilters["riskPreset"],
): PositionsWidgetRow[] => {
  if (preset === "none") {
    return rows;
  }
  const sorted = [...rows];
  if (preset === "worst-pnl") {
    return sorted.sort(
      (left, right) =>
        (left.intradayPlValue ?? 0) - (right.intradayPlValue ?? 0),
    );
  }
  if (preset === "largest-weight") {
    return sorted.sort(
      (left, right) =>
        Math.abs(right.marketValueAmount ?? 0) -
        Math.abs(left.marketValueAmount ?? 0),
    );
  }
  return sorted.sort(
    (left, right) => (left.daysToExpiry ?? 999) - (right.daysToExpiry ?? 999),
  );
};

/**
 * Clusters each underlying's rows together in first-appearance order, so an
 * equity and its option legs read as one block without a nested row model.
 */
export const groupPositionRowsByUnderlying = (
  rows: readonly PositionsWidgetRow[],
): PositionsWidgetRow[] => {
  const groups = new Map<string, PositionsWidgetRow[]>();
  for (const row of rows) {
    const existing = groups.get(row.rollSymbol);
    if (existing) {
      existing.push(row);
    } else {
      groups.set(row.rollSymbol, [row]);
    }
  }
  return [...groups.values()].flat();
};

export const groupPositionRowsByAssetClass = (
  rows: readonly PositionsWidgetRow[],
): PositionsWidgetRow[] => {
  const groups = new Map<string, PositionsWidgetRow[]>();
  for (const row of rows) {
    const existing = groups.get(row.assetClass);
    if (existing) {
      existing.push(row);
    } else {
      groups.set(row.assetClass, [row]);
    }
  }
  return [...groups.values()].flat();
};

export const arrangePositionRows = (
  rows: readonly PositionsWidgetRow[],
  filters: PositionsWidgetFilters,
  watchedSymbols: ReadonlySet<string> = new Set(),
): PositionsWidgetRow[] => {
  const filtered = applyPositionsRiskPreset(
    filterPositionRows(rows, filters, watchedSymbols),
    filters.riskPreset,
  );
  if (filters.grouping === "underlying") {
    return groupPositionRowsByUnderlying(filtered);
  }
  if (filters.grouping === "assetClass") {
    return groupPositionRowsByAssetClass(filtered);
  }
  return filtered;
};

export const getActivePositionsFilterCount = (
  filters: PositionsWidgetFilters,
): number =>
  Number(filters.assetClass !== "all") +
  Number(filters.side !== "all") +
  Number(
    filters.maxDaysToExpiry !== "all" && filters.maxDaysToExpiry.trim() !== "",
  ) +
  Number(filters.positionState !== "open") +
  Number(filters.grouping !== "flat") +
  Number(filters.source !== "all") +
  Number(filters.riskPreset !== "none") +
  Number(filters.watchlistOnly) +
  Number(filters.flaggedOnly) +
  Number(filters.stagedOnly);

export const getActiveAdvancedPositionsFilterCount = (
  filters: PositionsWidgetFilters,
): number =>
  Number(
    filters.maxDaysToExpiry !== "all" && filters.maxDaysToExpiry.trim() !== "",
  );

export const getPositionsFiltersValidationError = (
  filters: PositionsWidgetFilters,
): string | undefined => {
  const raw = filters.maxDaysToExpiry.trim();
  if (!raw || raw === "all") {
    return undefined;
  }
  const limit = Number(raw);
  if (!Number.isFinite(limit) || !Number.isInteger(limit)) {
    return "Enter days to expiry as a whole number.";
  }
  return limit < 0 ? "Days to expiry cannot be negative." : undefined;
};

export const hasAppliedPositionsFilters = (
  filters: PositionsWidgetFilters,
): boolean =>
  filters.search.trim() !== "" || getActivePositionsFilterCount(filters) > 0;

// ---------------------------------------------------------------------------
// Order-ticket seeding
// ---------------------------------------------------------------------------

/** Closing a long sells, closing a short buys. */
export const getPositionCloseSide = (
  positionSide: PositionsWidgetRow["positionSide"],
): "buy" | "sell" | undefined => {
  if (positionSide === "long") {
    return "sell";
  }
  return positionSide === "short" ? "buy" : undefined;
};

/** Fractional shares are supported, so only float noise is trimmed. */
export const roundPositionQuantity = (value: number): number =>
  Number.parseFloat(value.toFixed(8));

export const createPositionCloseSeed = (
  row: PositionsWidgetRow,
  ratio: number = 1,
): PositionsTicketSeed => {
  const side = getPositionCloseSide(row.positionSide);
  const quantity =
    row.quantityValue === undefined
      ? undefined
      : roundPositionQuantity(row.quantityValue * ratio);

  return {
    symbol: row.symbol,
    ...(side ? { side } : {}),
    ...(quantity !== undefined && quantity > 0 ? { quantity } : {}),
    ...(side
      ? { positionIntent: side === "sell" ? "sell_to_close" : "buy_to_close" }
      : {}),
  };
};
